import { useEffect, useRef, useState, type FormEvent } from 'react';
import QRCode from 'qrcode';

import './styles.css';

import { createRemoteAppTransportFromSession, RemoteConflictError } from './remoteTransport.js';
import { hasLocalBridge, LocalAppTransport } from './localTransport.js';
import type { ClientView, DocumentView, WorkspaceTransport } from './foundation.js';
import {
  createBrowserPairingOffer,
  registerBrowserPairingOffer,
  type BrowserPairingOffer,
} from '@mlo-local/connector/pairing';
import { BrowserWebRtcSession } from '@mlo-local/connector/webrtcSession';
import { EntityTypeV1, PresenceStateV1, type PresenceV1 } from '@mlo-local/protocol';
import { parseConfiguredIceServers, parseIceTransportPolicy } from './iceServers.js';

const CLIENT_STATUSES = [
  'LEAD',
  'PRE_QUALIFIED',
  'ACTIVE',
  'PROCESSING',
  'UNDERWRITING',
  'CLEAR_TO_CLOSE',
  'CLOSED',
  'DENIED',
] as const;

export function App() {
  const canvas = useRef<HTMLCanvasElement>(null);
  const session = useRef<BrowserWebRtcSession | null>(null);
  const presenceTimers = useRef(new Map<string, ReturnType<typeof globalThis.setTimeout>>());
  const [localMode, setLocalMode] = useState(() => hasLocalBridge());
  const [transport, setTransport] = useState<WorkspaceTransport | null>(() =>
    hasLocalBridge() ? new LocalAppTransport() : null,
  );
  const [status, setStatus] = useState('DISCONNECTED');
  const [pairingStatus, setPairingStatus] = useState(
    'Create an offer for this browser, then scan it with the phone.',
  );
  const [pairing, setPairing] = useState<BrowserPairingOffer | null>(null);
  const [clients, setClients] = useState<readonly ClientView[]>([]);
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null);
  const [clientQuery, setClientQuery] = useState('');
  const [clientStatusFilter, setClientStatusFilter] = useState('');
  const [includeArchived, setIncludeArchived] = useState(false);
  const [documents, setDocuments] = useState<readonly DocumentView[]>([]);
  const [presence, setPresence] = useState<readonly PresenceV1[]>([]);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [documentCategory, setDocumentCategory] = useState('INCOME');
  const [documentCondition, setDocumentCondition] = useState('');
  const [documentStatus, setDocumentStatus] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [editName, setEditName] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editStatus, setEditStatus] = useState('LEAD');
  const [editRevision, setEditRevision] = useState(0);
  const [mutationStatus, setMutationStatus] = useState<string | null>(null);
  const [conflict, setConflict] = useState<ClientView | null>(null);
  const [mutationPending, setMutationPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [syncStatus, setSyncStatus] = useState<string | null>(null);
  const [customFields, setCustomFields] = useState<Record<string, string>>({});
  const [newFieldKey, setNewFieldKey] = useState('');
  const [newFieldValue, setNewFieldValue] = useState('');
  const [editCustomFields, setEditCustomFields] = useState<Record<string, string>>({});
  const [editNewFieldKey, setEditNewFieldKey] = useState('');
  const [editNewFieldValue, setEditNewFieldValue] = useState('');
  const importInputRef = useRef<HTMLInputElement>(null);
  const localTransportCreated = useRef(false);

  const selectedClient = clients.find((client) => client.id === selectedClientId) ?? null;

  useEffect(() => {
    if (localMode || localTransportCreated.current || !hasLocalBridge()) return;
    localTransportCreated.current = true;
    setLocalMode(true);
    setTransport(new LocalAppTransport());
  }, [localMode]);

  useEffect(() => {
    if (!transport) return;
    const unsubscribe = transport.subscribe((next) => {
      setClients(next);
      setStatus(transport.status);
    });
    void transport
      .connect()
      .then(async () => {
        const listed = await transport.listClients({
          query: clientQuery,
          ...(clientStatusFilter ? { status: clientStatusFilter } : {}),
          includeArchived,
        });
        setClients(listed);
        setSelectedClientId((current) => current ?? listed[0]?.id ?? null);
      })
      .catch((error: unknown) =>
        setPairingStatus(error instanceof Error ? error.message : 'SESSION_FAILED'),
      );
    return () => {
      unsubscribe();
      transport.close();
      session.current?.close();
      session.current = null;
    };
  }, [transport]);

  useEffect(() => {
    if (!transport) {
      setPresence([]);
      return;
    }
    const unsubscribe = transport.subscribePresence((next) => {
      const key = presenceKey(next);
      const previousTimer = presenceTimers.current.get(key);
      if (previousTimer !== undefined) globalThis.clearTimeout(previousTimer);
      setPresence((current) => {
        const withoutCurrent = current.filter((item) => presenceKey(item) !== key);
        return [...withoutCurrent, next];
      });
      const timer = globalThis.setTimeout(() => {
        setPresence((current) => current.filter((item) => presenceKey(item) !== key));
        presenceTimers.current.delete(key);
      }, 10_000);
      presenceTimers.current.set(key, timer);
    });
    return () => {
      unsubscribe();
      presenceTimers.current.forEach((timer) => globalThis.clearTimeout(timer));
      presenceTimers.current.clear();
    };
  }, [transport]);

  useEffect(() => {
    if (!transport || !selectedClientId || status !== 'CONNECTED') return;
    void transport.announceViewing(EntityTypeV1.CLIENT, selectedClientId).catch(() => undefined);
  }, [transport, selectedClientId, status]);

  useEffect(() => {
    if (status === 'DISCONNECTED') setPresence([]);
  }, [status]);

  useEffect(() => {
    if (!transport || !selectedClientId) {
      setDocuments([]);
      return;
    }
    let current = true;
    const unsubscribe = transport.subscribeDocuments(selectedClientId, (next) => {
      setDocuments(next);
    });
    void transport
      .listDocuments(selectedClientId)
      .then((next) => {
        if (current) setDocuments(next);
      })
      .catch((cause: unknown) => {
        if (current) setDocumentStatus(cause instanceof Error ? cause.message : 'DOCUMENTS_FAILED');
      });
    return () => {
      current = false;
      unsubscribe();
    };
  }, [transport, selectedClientId]);

  useEffect(() => {
    if (!selectedClient) {
      setEditName('');
      setEditEmail('');
      setEditStatus('LEAD');
      setEditCustomFields({});
      return;
    }
    setEditName(selectedClient.name);
    setEditEmail(selectedClient.email);
    setEditStatus(selectedClient.status === 'INACTIVE' ? 'LEAD' : selectedClient.status);
    setEditCustomFields(selectedClient.customFields ? { ...selectedClient.customFields } : {});
    setEditRevision(selectedClient.revision);
    setConflict(null);
  }, [selectedClientId]);

  async function createPairingOffer(): Promise<void> {
    setStatus('CONNECTING');
    setPairingStatus('Creating an expiring offer…');
    try {
      const runtimeEnv = (import.meta as unknown as { env?: Record<string, unknown> }).env ?? {};
      const root = document.querySelector('#root');
      const iceServers = parseConfiguredIceServers(
        runtimeEnv.VITE_ICE_SERVERS ?? root?.getAttribute('data-ice-servers') ?? undefined,
      );
      const iceTransportPolicy = parseIceTransportPolicy(
        runtimeEnv.VITE_ICE_TRANSPORT_POLICY ?? root?.getAttribute('data-ice-transport-policy'),
      );
      const relayOrigin =
        (typeof runtimeEnv.VITE_RELAY_ORIGIN === 'string' && runtimeEnv.VITE_RELAY_ORIGIN) ||
        document.querySelector('#root')?.getAttribute('data-relay-origin') ||
        window.location.origin;
      const signalingOrigin =
        (typeof runtimeEnv.VITE_SIGNALING_ORIGIN === 'string' &&
          runtimeEnv.VITE_SIGNALING_ORIGIN) ||
        document.querySelector('#root')?.getAttribute('data-signaling-origin') ||
        `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}`;
      const allowInsecureLoopback =
        window.location.protocol === 'http:' &&
        ['localhost', '127.0.0.1', '[::1]'].includes(window.location.hostname);
      const result = await createBrowserPairingOffer({
        connectorOrigin: window.location.origin,
        signalingOrigin,
        browserLabel: navigator.userAgent,
        allowInsecureLoopback,
      });
      await registerBrowserPairingOffer({ relayOrigin, result, allowInsecureLoopback });
      if (canvas.current) {
        await QRCode.toCanvas(canvas.current, result.payload, {
          errorCorrectionLevel: 'M',
          margin: 2,
          width: 320,
        });
      }
      const nextSession = new BrowserWebRtcSession({
        pairing: result,
        iceServers,
        iceTransportPolicy,
        onState: (nextState) => setStatus(nextState),
        onError: (nextError) => setPairingStatus(nextError.message),
      });
      session.current = nextSession;
      setPairing(result);
      setPairingStatus(
        'Offer ready. Scan the QR code with the phone and approve the browser there.',
      );
      const nextTransport: WorkspaceTransport =
        await createRemoteAppTransportFromSession(nextSession);
      setTransport(nextTransport);
      setPairingStatus('Phone approved. Loading the private portfolio…');
    } catch (error) {
      session.current?.close();
      session.current = null;
      setStatus('DISCONNECTED');
      setPairingStatus(error instanceof Error ? error.message : 'PAIRING_FAILED');
    }
  }

  async function createClient(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setConflict(null);
    setMutationStatus('Saving client…');
    const activeTransport = transport;
    if (!activeTransport) return;
    setMutationPending(true);
    try {
      await activeTransport.createClient({
        name,
        email,
        ...(Object.keys(customFields).length > 0 ? { customFields } : {}),
      });
      setName('');
      setEmail('');
      setCustomFields({});
      setNewFieldKey('');
      setNewFieldValue('');
      setMutationStatus('Client saved');
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'CREATE_FAILED');
      setMutationStatus('Client was not saved');
    } finally {
      setMutationPending(false);
    }
  }

  function exportClientsJson(): void {
    try {
      const payload = JSON.stringify(
        {
          version: 1,
          exportedAt: new Date().toISOString(),
          clients,
        },
        null,
        2,
      );
      const blob = new Blob([payload], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const anchor = window.document.createElement('a');
      anchor.href = url;
      anchor.download = `clients-export-${new Date().toISOString().slice(0, 10)}.json`;
      anchor.click();
      window.setTimeout(() => URL.revokeObjectURL(url), 0);
      setSyncStatus(`Exported ${clients.length} client(s)`);
    } catch (cause) {
      setSyncStatus(cause instanceof Error ? cause.message : 'EXPORT_FAILED');
    }
  }

  async function importClientsFromFile(event: React.ChangeEvent<HTMLInputElement>): Promise<void> {
    const file = event.target.files?.[0];
    if (!file || !transport) return;
    setSyncStatus('Reading import file…');
    try {
      const text = await file.text();
      const parsed = JSON.parse(text) as
        | {
            clients?: Array<{ name?: unknown; email?: unknown; status?: unknown }>;
          }
        | Array<{ name?: unknown; email?: unknown; status?: unknown }>;
      const rawList: Array<{ name?: unknown; email?: unknown; status?: unknown }> = Array.isArray(
        parsed,
      )
        ? parsed
        : Array.isArray(parsed?.clients)
          ? parsed.clients
          : [];
      if (rawList.length === 0) {
        setSyncStatus('No valid clients found in file');
        return;
      }
      let count = 0;
      for (const item of rawList) {
        if (typeof item?.name === 'string' && item.name.trim() !== '') {
          const clientEmail = typeof item.email === 'string' ? item.email.trim() : '';
          const importedCustomFields: Record<string, string> | undefined =
            item &&
            typeof (item as { customFields?: unknown }).customFields === 'object' &&
            (item as { customFields?: Record<string, unknown> }).customFields !== null
              ? Object.fromEntries(
                  Object.entries(
                    (item as { customFields: Record<string, unknown> }).customFields,
                  ).filter(
                    (entry): entry is [string, string] =>
                      typeof entry[0] === 'string' && typeof entry[1] === 'string',
                  ),
                )
              : undefined;
          await transport.createClient({
            name: item.name.trim(),
            email: clientEmail,
            ...(importedCustomFields && Object.keys(importedCustomFields).length > 0
              ? { customFields: importedCustomFields }
              : {}),
          });
          count++;
        }
      }
      setSyncStatus(`Imported ${count} client(s) to phone`);
      await refreshClients();
    } catch (cause) {
      setSyncStatus(cause instanceof Error ? cause.message : 'IMPORT_FAILED');
    } finally {
      if (event.target) event.target.value = '';
    }
  }

  async function refreshClients(
    event?: FormEvent<HTMLFormElement>,
    statusFilter = clientStatusFilter,
  ): Promise<void> {
    event?.preventDefault();
    if (!transport) return;
    try {
      const listed = await transport.listClients({
        query: clientQuery,
        ...(statusFilter ? { status: statusFilter } : {}),
        includeArchived,
      });
      setClients(listed);
      setSelectedClientId((current) =>
        current && listed.some((client) => client.id === current)
          ? current
          : (listed[0]?.id ?? null),
      );
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'CLIENTS_FAILED');
    }
  }

  async function updateClient(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setConflict(null);
    const activeTransport = transport;
    if (!activeTransport?.updateClient || !selectedClient) return;
    setMutationPending(true);
    setMutationStatus('Saving client…');
    try {
      const saved = await activeTransport.updateClient({
        id: selectedClient.id,
        baseRevision: editRevision,
        name: editName,
        email: editEmail,
        status: editStatus,
        customFields: editCustomFields,
      });
      setEditRevision(saved.revision);
      setMutationStatus('Client saved');
    } catch (cause) {
      if (cause instanceof RemoteConflictError) {
        setConflict(cause.current);
        setEditName(cause.current.name);
        setEditEmail(cause.current.email);
        setEditStatus(cause.current.status);
        setEditCustomFields({ ...cause.current.customFields });
        setEditRevision(cause.current.revision);
        setError(
          `Conflict: this client changed in another browser (now revision ${cause.current.revision}). Review the current values and save again.`,
        );
      } else {
        setError(cause instanceof Error ? cause.message : 'UPDATE_FAILED');
      }
      setMutationStatus('Client was not saved');
    } finally {
      setMutationPending(false);
    }
  }

  async function archiveSelectedClient(): Promise<void> {
    const activeTransport = transport;
    if (!activeTransport?.archiveClient || !selectedClient) return;
    setError(null);
    setConflict(null);
    setMutationPending(true);
    setMutationStatus('Archiving client…');
    try {
      await activeTransport.archiveClient({
        id: selectedClient.id,
        baseRevision: selectedClient.revision,
      });
      setMutationStatus('Client archived');
      setSelectedClientId(null);
    } catch (cause) {
      if (cause instanceof RemoteConflictError) {
        setConflict(cause.current);
        setEditName(cause.current.name);
        setEditEmail(cause.current.email);
        setEditStatus(cause.current.status);
        setEditCustomFields({ ...cause.current.customFields });
        setEditRevision(cause.current.revision);
        setError(
          `Conflict: this client changed in another browser (now revision ${cause.current.revision}). Review the current values and save again.`,
        );
      } else {
        setError(cause instanceof Error ? cause.message : 'ARCHIVE_FAILED');
      }
      setMutationStatus('Client was not archived');
    } finally {
      setMutationPending(false);
    }
  }

  async function uploadDocument(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!transport || !selectedClientId || !uploadFile) {
      setDocumentStatus('Choose a client and file first');
      return;
    }
    setDocumentStatus('Uploading encrypted file…');
    try {
      const prepared = await transport.prepareDocumentUpload({
        clientId: selectedClientId,
        file: uploadFile,
        category: documentCategory,
        notes: documentCondition.trim() ? `Condition: ${documentCondition.trim()}` : '',
      });
      await transport.uploadDocument(prepared.operationId, prepared.prepared, uploadFile);
      setUploadFile(null);
      setDocumentCondition('');
      setDocumentStatus('File committed to the phone');
      setDocuments(await transport.listDocuments(selectedClientId));
    } catch (cause) {
      setDocumentStatus(cause instanceof Error ? cause.message : 'UPLOAD_FAILED');
    }
  }

  async function downloadDocument(document: DocumentView) {
    if (!transport?.downloadDocument) return;
    setDocumentStatus(`Preparing ${document.displayName}…`);
    try {
      const result = await transport.downloadDocument(document.activeFileVersionId);
      const url = URL.createObjectURL(result.file);
      const anchor = window.document.createElement('a');
      anchor.href = url;
      anchor.download = result.name;
      anchor.click();
      window.setTimeout(() => URL.revokeObjectURL(url), 0);
      setDocumentStatus(`${document.displayName} downloaded`);
    } catch (cause) {
      setDocumentStatus(cause instanceof Error ? cause.message : 'DOWNLOAD_FAILED');
    }
  }

  return (
    <main className="foundation-shell">
      <p className="eyebrow">Phone-hosted workspace</p>
      <h1>The phone is the application host.</h1>
      <p>
        This browser holds approved session state in memory only. Reloading or ending the session
        clears its view; the phone remains the authority.
      </p>
      <p className="transport-status" data-status={status}>
        Session: {status}
      </p>
      {transport && status === 'CONNECTED' ? (
        <>
          <form
            onSubmit={(event) => {
              void createClient(event);
            }}
            className="client-form"
          >
            <label>
              Name
              <input value={name} onChange={(event) => setName(event.target.value)} required />
            </label>
            <label>
              Email
              <input
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                required
              />
            </label>
            <div className="custom-fields-panel" role="region" aria-label="Custom loan attributes">
              <h3>Custom Loan Attributes</h3>
              <div className="custom-field-row">
                <input
                  placeholder="Attribute (e.g. Loan Program, Realtor)"
                  value={newFieldKey}
                  onChange={(e) => setNewFieldKey(e.target.value)}
                />
                <input
                  placeholder="Value (e.g. FHA, Jane Doe)"
                  value={newFieldValue}
                  onChange={(e) => setNewFieldValue(e.target.value)}
                />
                <button
                  type="button"
                  onClick={() => {
                    const k = newFieldKey.trim();
                    const v = newFieldValue.trim();
                    if (k && v) {
                      setCustomFields((curr) => ({ ...curr, [k]: v }));
                      setNewFieldKey('');
                      setNewFieldValue('');
                    }
                  }}
                >
                  Add
                </button>
              </div>
              {Object.keys(customFields).length > 0 ? (
                <div className="custom-fields-preview">
                  {Object.entries(customFields).map(([k, v]) => (
                    <span
                      key={k}
                      className="custom-field-tag"
                      onClick={() =>
                        setCustomFields((curr) => {
                          const next = { ...curr };
                          delete next[k];
                          return next;
                        })
                      }
                      title="Click to remove"
                    >
                      {k}: {v} &times;
                    </span>
                  ))}
                </div>
              ) : null}
            </div>
            <button type="submit" disabled={mutationPending}>
              {mutationPending ? 'Saving…' : 'Create client'}
            </button>
          </form>
          {error ? <p role="alert">{error}</p> : null}
          {mutationStatus ? <p role="status">{mutationStatus}</p> : null}
          <h2>Clients ({clients.length})</h2>
          <form onSubmit={(event) => void refreshClients(event)} className="client-form">
            <label>
              Search clients
              <input
                value={clientQuery}
                onChange={(event) => setClientQuery(event.target.value)}
                placeholder="Name or email"
              />
            </label>
            <label>
              Status filter
              <select
                value={clientStatusFilter}
                onChange={(event) => setClientStatusFilter(event.target.value)}
              >
                <option value="">All active statuses</option>
                {CLIENT_STATUSES.map((statusOption) => (
                  <option key={statusOption} value={statusOption}>
                    {statusOption.replaceAll('_', ' ')}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <input
                type="checkbox"
                checked={includeArchived}
                onChange={(event) => setIncludeArchived(event.target.checked)}
              />{' '}
              Include archived
            </label>
            <button type="submit">Apply filters</button>
          </form>
          <div className="sync-actions" role="region" aria-label="Sync actions">
            <button type="button" onClick={() => exportClientsJson()}>
              Export clients (JSON)
            </button>
            <button type="button" onClick={() => importInputRef.current?.click()}>
              Import clients (JSON)
            </button>
            <input
              ref={importInputRef}
              type="file"
              accept="application/json,.json"
              style={{ display: 'none' }}
              onChange={(e) => void importClientsFromFile(e)}
            />
            {syncStatus ? (
              <span className="sync-status" role="status">
                {syncStatus}
              </span>
            ) : null}
          </div>
          {presence.length > 0 ? (
            <section aria-label="Active browsers">
              <h2>Active browsers ({presence.length})</h2>
              <ul className="client-list">
                {presence.map((item) => (
                  <li key={presenceKey(item)}>
                    <strong>{item.actorDisplayLabel || 'Browser'}</strong>
                    <span>{PresenceStateV1[item.state] ?? 'Viewing'}</span>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}
          <label>
            Portfolio client
            <select
              value={selectedClientId ?? ''}
              onChange={(event) => setSelectedClientId(event.target.value || null)}
            >
              <option value="" disabled>
                Select a client
              </option>
              {clients.map((client) => (
                <option key={client.id} value={client.id}>
                  {client.name}
                </option>
              ))}
            </select>
          </label>
          <div className="pipeline-stages-bar" role="group" aria-label="Pipeline stages">
            <button
              type="button"
              className={`pipeline-stage-tab ${clientStatusFilter === '' ? 'active' : ''}`}
              aria-pressed={clientStatusFilter === ''}
              onClick={() => {
                setClientStatusFilter('');
                void refreshClients(undefined, '');
              }}
            >
              All
            </button>
            {CLIENT_STATUSES.map((st) => {
              return (
                <button
                  key={st}
                  type="button"
                  className={`pipeline-stage-tab ${clientStatusFilter === st ? 'active' : ''}`}
                  aria-pressed={clientStatusFilter === st}
                  onClick={() => {
                    setClientStatusFilter(st);
                    void refreshClients(undefined, st);
                  }}
                >
                  {st.replaceAll('_', ' ')}
                </button>
              );
            })}
          </div>
          <ul className="client-list">
            {clients.map((client) => (
              <li
                key={client.id}
                className={`client-card-item ${selectedClientId === client.id ? 'selected' : ''}`}
                onClick={() => setSelectedClientId(client.id)}
                title="Click to edit or advance pipeline stage"
              >
                <strong>{client.name}</strong>
                <span>{client.email}</span>
                <span className="client-stage-badge">{client.status.replaceAll('_', ' ')}</span>
                {client.customFields && Object.keys(client.customFields).length > 0 ? (
                  <div className="custom-fields-preview">
                    {Object.entries(client.customFields).map(([k, v]) => (
                      <span key={k} className="custom-field-tag">
                        {k}: {v}
                      </span>
                    ))}
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
          {selectedClientId ? (
            <>
              {selectedClient ? (
                <section aria-label="Client details">
                  <h2>Edit client</h2>
                  <form onSubmit={(event) => void updateClient(event)} className="client-form">
                    <label>
                      Name
                      <input
                        value={editName}
                        onChange={(event) => setEditName(event.target.value)}
                        required
                      />
                    </label>
                    <label>
                      Email
                      <input
                        type="email"
                        value={editEmail}
                        onChange={(event) => setEditEmail(event.target.value)}
                        required
                      />
                    </label>
                    <label>
                      Status
                      <select
                        value={editStatus}
                        onChange={(event) => setEditStatus(event.target.value)}
                      >
                        {CLIENT_STATUSES.map((statusOption) => (
                          <option key={statusOption} value={statusOption}>
                            {statusOption.replaceAll('_', ' ')}
                          </option>
                        ))}
                      </select>
                    </label>
                    <div
                      className="custom-fields-panel"
                      role="region"
                      aria-label="Edit custom loan attributes"
                    >
                      <h3>Custom Loan Attributes</h3>
                      <div className="custom-field-row">
                        <input
                          placeholder="Attribute (e.g. Loan Program, Realtor)"
                          value={editNewFieldKey}
                          onChange={(e) => setEditNewFieldKey(e.target.value)}
                        />
                        <input
                          placeholder="Value (e.g. FHA, Jane Doe)"
                          value={editNewFieldValue}
                          onChange={(e) => setEditNewFieldValue(e.target.value)}
                        />
                        <button
                          type="button"
                          onClick={() => {
                            const k = editNewFieldKey.trim();
                            const v = editNewFieldValue.trim();
                            if (k && v) {
                              setEditCustomFields((curr) => ({ ...curr, [k]: v }));
                              setEditNewFieldKey('');
                              setEditNewFieldValue('');
                            }
                          }}
                        >
                          Add
                        </button>
                      </div>
                      {Object.keys(editCustomFields).length > 0 ? (
                        <div className="custom-fields-preview">
                          {Object.entries(editCustomFields).map(([k, v]) => (
                            <span
                              key={k}
                              className="custom-field-tag"
                              onClick={() =>
                                setEditCustomFields((curr) => {
                                  const next = { ...curr };
                                  delete next[k];
                                  return next;
                                })
                              }
                              title="Click to remove"
                            >
                              {k}: {v} &times;
                            </span>
                          ))}
                        </div>
                      ) : null}
                    </div>
                    <button type="submit" disabled={mutationPending}>
                      {mutationPending ? 'Saving…' : `Save revision ${editRevision + 1}`}
                    </button>
                    <button
                      type="button"
                      disabled={mutationPending}
                      onClick={() => void archiveSelectedClient()}
                    >
                      Archive client
                    </button>
                  </form>
                  {conflict ? (
                    <p role="status">
                      Current phone revision: {conflict.revision}. The fields above were refreshed;
                      save again to apply your reviewed values.
                    </p>
                  ) : null}
                </section>
              ) : null}
              <section aria-label="Private documents">
                <h2>Private documents ({documents.length})</h2>
                <form onSubmit={(event) => void uploadDocument(event)} className="client-form">
                  <label>
                    File
                    <input
                      type="file"
                      onChange={(event) => setUploadFile(event.target.files?.[0] ?? null)}
                      required
                    />
                  </label>
                  <label>
                    Category
                    <select
                      value={documentCategory}
                      onChange={(e) => setDocumentCategory(e.target.value)}
                    >
                      <option value="INCOME">Income / Paystubs / W-2</option>
                      <option value="ASSETS">Assets / Bank Statements</option>
                      <option value="CREDIT">Credit / Liabilities</option>
                      <option value="PROPERTY">Property / Appraisal / Title</option>
                      <option value="DISCLOSURES">Disclosures / Authorizations</option>
                      <option value="CONDITIONS">Underwriting Condition</option>
                      <option value="GENERAL">General</option>
                    </select>
                  </label>
                  <label>
                    Condition / Tag
                    <input
                      placeholder="e.g. P01 - 2 Yrs Tax Returns, Borrower Earnest Money"
                      value={documentCondition}
                      onChange={(e) => setDocumentCondition(e.target.value)}
                    />
                  </label>
                  <button type="submit" disabled={!uploadFile}>
                    Upload to phone
                  </button>
                </form>
                <ul className="client-list">
                  {documents.map((document) => (
                    <li key={document.id}>
                      <strong>{document.displayName}</strong>
                      <span className="client-stage-badge">{document.category}</span>
                      {document.notes ? <small>{document.notes}</small> : null}
                      <button type="button" onClick={() => void downloadDocument(document)}>
                        Download
                      </button>
                    </li>
                  ))}
                </ul>
                {documentStatus ? <p role="status">{documentStatus}</p> : null}
              </section>
            </>
          ) : null}
          <button
            type="button"
            onClick={() => {
              if (localMode && transport.closeLocalSession) {
                void transport.closeLocalSession().then(() => window.location.reload());
                return;
              }
              transport.close();
              session.current?.close();
              session.current = null;
              setTransport(null);
              setStatus('DISCONNECTED');
              setPairingStatus('Session ended. Create a new offer to reconnect.');
            }}
          >
            End session and clear
          </button>
        </>
      ) : (
        <section className="pairing-panel" aria-live="polite">
          {localMode ? (
            <>
              <h2>On-device workspace</h2>
              <p>
                Select an Android profile, then refresh this view to open its private portfolio.
              </p>
              <button type="button" onClick={() => window.location.reload()}>
                Refresh after profile selection
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={() => void createPairingOffer()}
                disabled={status === 'CONNECTING'}
              >
                {status === 'CONNECTING' ? 'Waiting for phone approval…' : 'Create pairing offer'}
              </button>
              <p>{pairingStatus}</p>
              <canvas ref={canvas} width={320} height={320} aria-label="Pairing QR code" />
              <p>
                Manual code: <strong>{pairing?.manualCode ?? '—'}</strong>
              </p>
              <details>
                <summary>Canonical payload</summary>
                <code>{pairing?.payload ?? '—'}</code>
              </details>
            </>
          )}
        </section>
      )}
    </main>
  );
}

function presenceKey(presence: PresenceV1): string {
  return `${bytesKey(presence.sessionId)}:${presence.entityType}:${bytesKey(presence.entityId)}`;
}

function bytesKey(bytes: Uint8Array): string {
  return Array.from(bytes, (value) => value.toString(16).padStart(2, '0')).join('');
}
